
import ServiceTemplate from '../../ServiceTemplate';
import SEOHead from '../../../../components/SEOHead';

export default function ElectricalFaultDiagnosticsPage() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    serviceType: 'Electrical Fault Diagnostics',
    provider: {
      '@type': 'ElectricalContractor',
      name: 'Fresno Electrical Services',
      address: {
        '@type': 'PostalAddress',
        addressLocality: 'Fresno',
        addressRegion: 'CA',
        addressCountry: 'US',
      },
    },
    areaServed: {
      '@type': 'City',
      name: 'Fresno',
      '@id': 'https://en.wikipedia.org/wiki/Fresno,_California',
    },
    description:
      'Electrical fault diagnostics and troubleshooting in Fresno, CA. Find and fix electrical problems, circuit issues, and system faults quickly and accurately.',
    url: `${siteUrl}/services/testing-certification/electrical-fault-diagnostics`,
  };

  const features = [
    {
      icon: 'ri-search-eye-line',
      title: 'Advanced Fault Detection',
      description:
        'Sophisticated testing equipment identifies hidden electrical faults and intermittent problems',
    },
    {
      icon: 'ri-flashlight-line',
      title: 'Thermal Imaging',
      description:
        'Infrared cameras detect overheating circuits, loose connections, and failing components',
    },
    {
      icon: 'ri-tools-line',
      title: 'Circuit Testing',
      description:
        'Comprehensive circuit analysis to identify shorts, opens, ground faults, and voltage issues',
    },
    {
      icon: 'ri-alert-line',
      title: 'Breaker Diagnostics',
      description:
        'Test circuit breakers for proper operation, tripping issues, and safety performance',
    },
    {
      icon: 'ri-home-gear-line',
      title: 'Wiring Fault Location',
      description:
        'Pinpoint exact location of wiring faults, breaks, and connection problems',
    },
    {
      icon: 'ri-file-list-3-line',
      title: 'Root Cause Analysis',
      description: 'Identify underlying causes of electrical problems, not just symptoms',
    },
    {
      icon: 'ri-time-line',
      title: 'Fast Problem Resolution',
      description:
        'Efficient diagnostics minimize downtime and get your electrical system working quickly',
    },
    {
      icon: 'ri-file-text-line',
      title: 'Detailed Findings Report',
      description:
        'Clear documentation of faults found, causes identified, and repairs recommended',
    },
  ];

  const whatsIncluded = [
    'Licensed C-10 electrician diagnostics',
    'Advanced testing equipment',
    'Thermal imaging scans',
    'Circuit and voltage testing',
    'Breaker performance testing',
    'Wiring fault location',
    'Ground fault detection',
    'Root cause identification',
    'Repair recommendations and estimates',
    'Detailed diagnostic report',
  ];

  return (
    <>
      <SEOHead
        title="Electrical Fault Diagnostics Fresno CA | Electrical Troubleshooting & Testing"
        description="Electrical fault diagnostics and troubleshooting in Fresno, CA. Find and fix electrical problems fast with advanced testing equipment. Licensed electricians available now."
        canonical="/services/testing-certification/electrical-fault-diagnostics"
        keywords="electrical fault diagnostics Fresno, electrical troubleshooting, circuit testing Fresno, electrical problem diagnosis"
        schema={schema}
      />
      <ServiceTemplate
        heroTitle="Electrical Fault Diagnostics — Fresno, CA"
        heroSubtitle="Find and fix electrical problems fast with advanced diagnostic testing"
        heroImage="https://public.readdy.ai/ai/img_res/5b1c941b993fd423aa4ed782ced0fbfd.jpg"
        fresnoImage="https://readdy.ai/api/search-image?query=Close%20up%20of%20electrical%20diagnostic%20testing%20equipment%20including%20thermal%20imaging%20camera%20digital%20multimeter%20clamp%20meter%20and%20circuit%20tester%20arranged%20on%20table%20next%20to%20electrical%20panel%20with%20wiring%20visible%20bright%20professional%20setting&width=700&height=600&seq=fault-diag-fresno-001&orientation=portrait"
        fresnoContent="Electrical problems in your Fresno home or business? Our licensed electricians use advanced diagnostic equipment to quickly identify and resolve electrical faults. From flickering lights and tripped breakers to dead outlets and mysterious power issues, we pinpoint the exact cause and provide lasting solutions. Serving Woodward Park, Tower District, and all of Fresno County, we combine technical expertise with state-of-the-art testing tools to solve even the most challenging electrical problems."
        features={features}
        whatsIncluded={whatsIncluded}
        faqs={[
          {
            question: 'What electrical problems can you diagnose in Fresno?',
            answer:
              'We diagnose all electrical faults including tripped breakers, flickering lights, dead outlets, power surges, voltage fluctuations, ground faults, short circuits, overloaded circuits, intermittent power loss, burning smells, buzzing sounds, and mysterious electrical issues. Our Fresno electricians use thermal imaging, circuit testing, and advanced diagnostics to find problems other electricians miss.',
          },
          {
            question: 'How much does electrical fault diagnostics cost in Fresno?',
            answer:
              'Electrical diagnostics in Fresno typically cost $150–$300 for the initial assessment, which includes testing, fault identification, and repair estimates. Complex diagnostics requiring extensive testing may cost more. If you proceed with repairs, we often credit the diagnostic fee toward the repair cost. We provide upfront pricing before starting work.',
          },
          {
            question: 'How long does electrical fault diagnostics take in Fresno?',
            answer:
              'Most electrical diagnostics take 1–2 hours depending on problem complexity. Simple issues like a tripped breaker or bad outlet can be diagnosed in minutes. Complex problems like intermittent faults, hidden wiring issues, or system-wide problems may require more extensive testing. We work efficiently to minimize disruption to your Fresno home or business.',
          },
          {
            question: 'What equipment do you use for electrical fault diagnostics in Fresno?',
            answer:
              'We use professional‑grade diagnostic equipment including thermal imaging cameras (detect hot spots and overheating), digital multimeters (measure voltage, current, resistance), clamp meters (measure circuit loads), circuit tracers (locate wiring), ground fault testers, and specialized tools for breaker testing and fault location. This advanced equipment helps us diagnose problems quickly and accurately in Fresno.',
          },
          {
            question: 'Can you find intermittent electrical problems in Fresno homes?',
            answer:
              "Yes. Intermittent electrical problems are challenging but we have the expertise and equipment to diagnose them. We use thermal imaging to detect overheating that occurs intermittently, monitor circuits over time, test connections under load, and systematically eliminate potential causes. Our Fresno electricians have solved countless intermittent electrical problems that other electricians couldn't find.",
          },
          {
            question: 'Do you provide emergency electrical diagnostics in Fresno?',
            answer:
              "Yes. We offer emergency electrical diagnostics for urgent problems like power outages, burning smells, sparking outlets, or other dangerous electrical issues in Fresno. Our emergency electricians respond quickly with diagnostic equipment to identify and resolve safety hazards. We're available for emergency diagnostics throughout Fresno County.",
          },
        ]}
      />
    </>
  );
}
